import React, { useContext, useEffect, useMemo, useState } from 'react';
import { EntityValidations, ISpecificEntityFormProps, IZCoreAIChat, ZCoreAIChat, ZCoreAIChatUser } from '../../types';
import { LookupService } from '../../services';
import styles from '../../styles/cr.module.scss';
import { CrUserPicker } from '../cr/CrUserPicker';
import { CrTextField } from '../cr/CrTextField';
import { CrDropdown } from '../cr/CrDropdown';
import { EntityForm } from '../EntityForm';
import { DataContext } from '../DataContext';
import { IntegrationContext } from '../IntegrationContext';

class AiChatFormValidations extends EntityValidations {
    public Owners: string = null;
}

interface AiChatFormProps extends ISpecificEntityFormProps {

}

export const AiChatForm = (props: AiChatFormProps): React.ReactElement => {
    const { disableGroupManagement } = useContext(IntegrationContext);
    const { dataServices, lookupData, loadLookupData: { entityStatuses, users: { all: allUsers } } } = useContext(DataContext);

    useMemo(() => entityStatuses(), [entityStatuses]);
    useMemo(() => allUsers(), [allUsers]);

    const validateEntity = (aiChat: IZCoreAIChat): Promise<AiChatFormValidations> => {
        const errors = new AiChatFormValidations();

        if (aiChat.Title === null || aiChat.Title === '') {
            errors.Title = `Chat title is required`;
            errors.Valid = false;
        }
        else {
            errors.Title = null;
        }
        if (aiChat.ZCoreAIChatUsers === undefined || aiChat.ZCoreAIChatUsers === null || aiChat.ZCoreAIChatUsers.length < 1) {
            errors.Owners = `At least one owner is required`;
            errors.Valid = false;
        }
        else {
            errors.Owners = null;
        }

        return Promise.resolve(errors);
    };

    return (
        <EntityForm<IZCoreAIChat, AiChatFormValidations>
            {...props}
            entityName='Cloe AI Chat'
            renderFormFields={(changeHandlers, formState) => {
                const { FormData: aiChat, ValidationErrors: errors } = formState;
                return (
                    <div>
                        <CrTextField
                            label="Chat Title"
                            maxLength={500}
                            className={styles.formField}
                            required={true}
                            value={aiChat.Title}
                            onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'Title')}
                            errorMessage={errors.Title}
                        />

                        <CrTextField
                            label="Context"
                            multiline
                            rows={6}
                            maxLength={10000}
                            className={styles.formField}
                            value={aiChat.ChatContext}
                            onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'ChatContext')}
                            placeholder="This context will be automatically included with every prompt in this chat."
                        />

                        <CrDropdown
                            label="Summary Frequency"
                            className={styles.formField}
                            options={LookupService.entitiesToSelectableOptions([
                                { ID: 10, Title: "Every 10th prompt" },
                                { ID: 20, Title: "Every 20th prompt" },
                                { ID: 30, Title: "Every 30th prompt" },
                            ])}
                            selectedKey={aiChat.SummaryFrequency}
                            onChange={(_, o) => changeHandlers.changeDropdown(o, "SummaryFrequency")}
                        />

                        <CrUserPicker
                            label="Owner(s)"
                            className={styles.formField}
                            users={lookupData.Users?.All}
                            itemLimit={5}
                            required={true}
                            errorMessage={errors.Owners}
                            selectedUsers={aiChat.ZCoreAIChatUsers?.map(u => u.UserID)}
                            onChange={v => changeHandlers.changeMultiUserPicker(v, 'ZCoreAIChatUsers', new ZCoreAIChatUser(), 'UserID')}
                        />

                        <CrDropdown
                            label="Status"
                            className={styles.formField}
                            disabled={disableGroupManagement}
                            options={LookupService.entitiesToSelectableOptions(lookupData.EntityStatuses)}
                            selectedKey={aiChat.EntityStatusID}
                            onChange={(_, o) => changeHandlers.changeStatusDropdown(o, 'EntityStatusID')}
                        />

                    </div>
                );
            }}
            loadEntity={id => dataServices.zCoreAIChatService.read(id, true, true)}
            loadNewEntity={() => new ZCoreAIChat()}
            loadEntityValidations={() => new AiChatFormValidations()}
            onValidateEntity={validateEntity}
            onCreate={c => dataServices.zCoreAIChatService.create(c)}
            onUpdate={c => dataServices.zCoreAIChatService.update(c.ID, c)}
            parentEntities={dataServices.zCoreAIChatService.parentEntities}
            childEntities={[
                {
                    ObjectParentProperty: 'ZCoreAIChatUsers',
                    ParentIdProperty: 'ChatID',
                    ChildIdProperty: 'UserID',
                    ChildService: dataServices.zCoreAIChatUserService,
                },
            ]}
        />
    );
};
